import PercentIcon from "../../assets/icons/PercentIcon";
import HeartIcon from "../../assets/icons/HeartIcon";
import { Link, useLocation } from "react-router-dom";
import { useCartStore } from "../../store/cartStore";
import { addToCart, removeFromCart, getCart, transformProductsToCartItems } from "../../services/cartService";
import { useFavoritesStore } from "../../store/favoritesStore";
import { addToFavorites, getAllFavorites, removeFromFavorites } from "../../services/favoritesService";
import { Product } from "../../types";
import { useEffect, useRef, useState } from "react";
import Notification from "../toast/Notification";
// Убрали старую логику остатков по городам - теперь используется система складов

interface ProductCardProps {
  product: Product;
}

function ProductCard({ product }: ProductCardProps) {
  const location = useLocation();
  const { items: favoriteItems, addItem: addFavoriteLocal, removeItem: removeFavoriteLocal, setItems: setFavorites } = useFavoritesStore();
  const { items, addItem, updateItemQuantity, removeItem, setCart } = useCartStore();
  const isLoggedIn = !!localStorage.getItem("token");
  const isCartPage = location.pathname === "/cart"

  // Убрали старую логику остатков по городам - теперь используется система складов
  const [stock, setStock] = useState(0);

  const [notification, setNotification] = useState<{ id: number, text: string; status: "success" | "error" | "info" } | null>(null);

  // Проверяем, есть ли товар в корзине
  const cartItem = items.find((i) => i.id === product.id);
  const isFavorite = favoriteItems.some((i) => i.id === product.id);

  const [isDragging, setIsDragging] = useState(false);
  const dragStartPos = useRef({ x: 0, y: 0 });

  useEffect(() => {
    if (product) {
      // Теперь используем stock_quantity из продукта или 0 по умолчанию
      setStock(product.stock_quantity || 0);
    }
  }, [product]);

  const handleMouseDown = (e: React.MouseEvent) => {
    dragStartPos.current = { x: e.clientX, y: e.clientY };
    setIsDragging(false);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (e.buttons === 1) {
      const deltaX = Math.abs(e.clientX - dragStartPos.current.x);
      const deltaY = Math.abs(e.clientY - dragStartPos.current.y);
      if (deltaX > 5 || deltaY > 5) {
        setIsDragging(true);
      }
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    if (isDragging) {
      e.preventDefault();
      setIsDragging(false);
    }
  };

  const refreshCartFromServer = async () => {
    const response = await getCart();
    setCart(transformProductsToCartItems(response.result));
  };

  const refreshFavoritesFromServer = async () => {
    const response = await getAllFavorites();
    setFavorites(response.result);
  };

  // -------------------
  // Логика избранного
  // -------------------
  const handleFavoriteClick = async (product: Product) => {
    if (isLoggedIn) {
      try {
        if (isFavorite) {
          await removeFromFavorites(product.id);
        } else {
          await addToFavorites(product.id);
        }
        await refreshFavoritesFromServer();
      } catch (error) {
        console.error("Ошибка при обновлении избранного:", error);
      }
    } else {
      // Локальная логика
      if (isFavorite) {
        removeFavoriteLocal(product.id);
      } else {
        addFavoriteLocal(product);
      }
    }
  };

  // -------------------
  // Логика корзины
  // -------------------
  const handleIncrement = async () => {
    // 1. Если нет стока (всё распродано) — выходим
    if (stock === 0) {
      setNotification({ id: Date.now(), text: `Нет в наличии`, status: "info" });
      return;
    }

    // 2. Если уже есть в корзине, проверить, не достигли ли максимума
    if (cartItem && cartItem.quantity && stock && cartItem.quantity >= stock) {
      setNotification({ id: Date.now(), text: `Нельзя добавить больше, чем ${stock} штук`, status: "info" });
      return;
    }

    // 3. Если всё ок, добавляем
    if (isLoggedIn) {
      try {
        await addToCart(product.id, 1);
        await refreshCartFromServer();
      } catch (error) {
        console.error("Ошибка при добавлении товара в корзину:", error);
      }
    } else {
      if (cartItem) {
        updateItemQuantity(product.id, 1);
      } else {
        addItem(product);
      }
    }
  };

  const handleDecrement = async () => {
    if (!cartItem) return;

    if (isLoggedIn) {
      try {
        if (cartItem.quantity && cartItem.quantity > 1) {
          await addToCart(product.id, -1);
        } else {
          await removeFromCart(product.id);
        }
        await refreshCartFromServer();
      } catch (error) {
        console.error("Ошибка при уменьшении количества товара:", error);
      }
    } else {
      if (cartItem.quantity && cartItem.quantity > 1) {
        updateItemQuantity(product.id, -1);
      } else {
        removeItem(product.id);
      }
    }
  };

  const handleRemoveItem = async () => {
    if (isLoggedIn) {
      try {
        await removeFromCart(product.id);
        await refreshCartFromServer();
      } catch (error) {
        console.error("Ошибка при удалении товара из корзины:", error);
      }
    } else {
      removeItem(product.id);
    }
  };

  return (
    <div className="block">
      <Link
        to={`/product/${product.id}`}
        onClick={handleClick}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
      >
        <div className="relative mb-4 md:h-[245px] h-[168px] group">
          <img
            src={product.cover_image ? `${product.cover_image}${product.updated_at ? `?v=${encodeURIComponent(product.updated_at)}` : ''}` : ''}
            alt={product.name}
            className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-110"
          />
          <button
            className="absolute top-3 right-3 p-1"
            onClick={(e) => {
              e.preventDefault();
              handleFavoriteClick(product);
            }}
          >
            <HeartIcon isFavorite={isFavorite} />
          </button>
        </div>
        <div>
          {product.category && (
            <h4 className="text-sm text-gray-500 truncate uppercase" title={product.category.name}>
              {product.category.name}
            </h4>
          )}
          <h3 className="font-bold text-lg line-clamp-1 mt-1 " title={product.name}>
            {product.name}
          </h3>
          <div className="flex items-center space-x-2 mt-2">
            <span className="text-xl font-bold">{product.price} ₸</span>
          </div>
          {/* Метка наличия */}
          {/* <div className="mt-1">{stockLabel}</div> */}
        </div>
      </Link>

      {cartItem ? (
        stock === 0 ? (
          <>
            <button
              className="mt-4 py-2 px-2 bg-gray-200 text-gray-400 cursor-not-allowed rounded"
              disabled
            >
              Нет в наличии
            </button>
            {isCartPage &&
              <button className="px-2 text-red-600 hover:text-red-800" onClick={handleRemoveItem}>
                ❌
              </button>
            }
          </>
        ) :        // Уже есть в корзине – показываем +/- блок
          <div className="flex items-center space-x-2 mt-4 border rounded justify-between max-w-36">
            <button
              className="px-4 py-2 font-bold text-black border-black hover:bg-gray-100"
              onClick={handleDecrement}
            >
              -
            </button>
            <span className="px-2">{cartItem.quantity}</span>
            <button
              className="px-4 py-2 font-bold text-black border-black hover:bg-gray-100"
              onClick={handleIncrement}
            >
              +
            </button>
          </div>
      ) : (stock === 0 ? (
        // Если товара нет, показываем серую кнопку (disabled)
        <button
          className="mt-4 py-2 px-2 bg-gray-200 text-gray-400 cursor-not-allowed rounded"
          disabled
        >
          Нет в наличии
        </button>
      ) : (
        // Товар есть, но ещё не в корзине
        <button
          className="mt-4 py-2 px-2 bg-[#2C2C2C] text-white rounded hover:bg-gray-800"
          onClick={handleIncrement}
        >
          В корзину
        </button>
      ))}
      {notification && <Notification id={notification.id || Date.now()} text={notification.text} status={notification.status} duration={5000} />}
    </div>
  );
}

export default ProductCard;
